import React, { PropsWithChildren, SyntheticEvent } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import { CookingTimeText } from '../CookingTimeText'
import { RecipeDietaryTagOverlay, RecipeGridRecipeTileImage as TileImage } from '@library/menu-page'
import { Title } from '../Title'
import { DietaryLabels } from '../DietaryLabels'
import { LabelWrapper } from '../Label/LabelWrapper'
import { SoldOutOverlay } from '../SoldOutOverlay'
import { CaloriesText } from '../Calories'
import { CollectionUUID, Recipe, RecipeUUID } from '@library/menu-service'
import { AddRecipeButton } from '../AddRecipeButton'
import { RecipeTilePurchaseInfo } from '../RecipeTilePurchaseInfo'
import { RecipeTileReadyToHeatBanner } from '../RecipeTileReadyToHeatBanner'
import { cssAddRecipeButtonContainer, cssRecipeTagHolder } from '../RecipeTile/styles'
import { ProteinText } from '../ProteinText'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { RecipeSubtitle } from '../RecipeSubtitle'
import { FlexPortionTileBadge } from '../Flex'
import { dietaryClaimAcronyms, getDietaryClaims } from '../../utils/getRecipeDietaryClaims'

const RecipeTagContainer = styled.span(() => ({
  ...cssRecipeTagHolder,
}))

type RecipeTileImageVariant = 'standard' | 'prominent'

type ImageContainerProps = PropsWithChildren<{
  variant?: RecipeTileImageVariant
}>

const BaseImageContainer = ({
  variant: _variant,
  ...props
}: ImageContainerProps & React.ComponentProps<typeof Box>) => <Box {...props} />

const imageSizeStylesByVariant: Record<RecipeTileImageVariant, React.CSSProperties> = {
  standard: {
    minHeight: '200px',
    maxHeight: '270px',
  },
  prominent: {
    minHeight: '230px',
    maxHeight: '300px',
    height: 'clamp(230px, 62vw, 300px)',
  },
}

const ImageContainer = withCitrusTheme(
  BaseImageContainer,
  (_theme, { variant = 'standard' }: ImageContainerProps) => ({
    ...imageSizeStylesByVariant[variant],
    borderRadius: '4px 4px 0 0',
    position: 'relative',
    overflow: 'hidden',
  }),
)

const AddRecipeButtonContainer = styled.span(() => ({
  ...cssAddRecipeButtonContainer,
}))

export type RecipeTileHeaderProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  menuPreferences?: MenuPreferences
  showCustomisedTileFooter?: boolean
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe?: (recipeId: RecipeUUID) => void
  removeRecipe?: (recipeId: RecipeUUID) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  index?: number
  alternativeOptions?: AlternativeOptions
  lazy?: boolean
  insideOverflow?: boolean
  showPurchaseInfo?: boolean
  numPortions: number
  quantity?: number
  showFlexPortionTileBadge?: boolean
  showFlexEducationTooltip?: boolean
  closeFlexEducationTooltip?: () => void
  isFlexPortionEditorOpen?: boolean
  onEditFlexPortions?: () => void
  isRecipeTagsCwcEnabled?: boolean
}

export const RecipeTileHeader = ({
  recipe,
  currentCollectionId: categoryId,
  menuPreferences,
  showCustomisedTileFooter = false,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  index,
  lazy = true,
  insideOverflow = false,
  showPurchaseInfo = true,
  numPortions,
  quantity = 0,
  showFlexPortionTileBadge = false,
  showFlexEducationTooltip = false,
  closeFlexEducationTooltip,
  isFlexPortionEditorOpen = false,
  onEditFlexPortions,
  isRecipeTagsCwcEnabled = false,
  alternativeOptions,
}: RecipeTileHeaderProps) => {
  const isHeatOnly = recipe.preparationType === 'heat-only'
  const hasAlternativeOptions =
    alternativeOptions?.optionsType !== 'none' && alternativeOptions?.optionsType !== undefined

  const showFlexEditor = showFlexPortionTileBadge && isInBasket && quantity > 0
  const showLabelWrapper = !showFlexEditor
  const showAddButton = addRecipe && removeRecipe && !showFlexEditor
  const showCaloriesText = isCaloriePreferenceSelected(menuPreferences?.display?.list)
  const imageVariant: RecipeTileImageVariant = isRecipeTagsCwcEnabled ? 'prominent' : 'standard'
  const dietaryOverlayTags = getDietaryClaims(recipe).map((claim) => ({
    type: claim,
    text: dietaryClaimAcronyms[claim],
  }))

  const handleOnClickRecipeButtonContainer = (e: SyntheticEvent) => {
    // Prevents opening recipe details modal when clicking on disabled add button
    if (isOutOfStock) e.stopPropagation()
  }

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  return (
    <Box
      width="100%"
      height="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      position={DisplayPosition.Relative}
    >
      <ImageContainer variant={imageVariant}>
        <SoldOutOverlay isOutOfStock={isOutOfStock} />
        <TileImage lazy={lazy} recipe={recipe} insideOverflow={insideOverflow} decorative />
        {isRecipeTagsCwcEnabled ? (
          <>
            {showLabelWrapper && (
              <LabelWrapper
                isOutOfStock={isOutOfStock}
                recipe={recipe}
                reserveCtaSpace
                isRecipeTagsCwcEnabled
              />
            )}
            <RecipeDietaryTagOverlay tags={dietaryOverlayTags} />
          </>
        ) : (
          <RecipeTagContainer>
            <DietaryLabels recipe={recipe} />
          </RecipeTagContainer>
        )}
        {showFlexEditor && (
          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.FlexEnd}
            position={DisplayPosition.Absolute}
            left="1rem"
            right="1rem"
            bottom="1rem"
            style={{ pointerEvents: 'none' }}
          >
            <Box
              display={Display.Flex}
              justifyContent={JustifyContent.FlexEnd}
              width="100%"
              onMouseDown={(event: React.MouseEvent) => {
                if (isFlexPortionEditorOpen) {
                  event.stopPropagation()
                }
              }}
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
              style={{ pointerEvents: 'none' }}
            >
              <FlexPortionTileBadge
                recipeCount={quantity}
                portionCount={numPortions}
                onEdit={() => onEditFlexPortions?.()}
                showEducationTooltip={showFlexEducationTooltip}
                onCloseEducationTooltip={closeFlexEducationTooltip}
              />
            </Box>
          </Box>
        )}
      </ImageContainer>

      <Box position={DisplayPosition.Relative} flexGrow={1} style={{ margin: '1rem' }}>
        <Box display={Display.Flex} paddingBottom={isRecipeTagsCwcEnabled ? 0 : 2}>
          {!isRecipeTagsCwcEnabled && showLabelWrapper && (
            <LabelWrapper
              isOutOfStock={isOutOfStock}
              recipe={recipe}
              reserveCtaSpace={Boolean(showAddButton)}
            />
          )}
          {showAddButton && (
            <AddRecipeButtonContainer onClick={handleOnClickRecipeButtonContainer}>
              <AddRecipeButton
                recipeUuid={recipe.id}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipe}
                removeRecipe={removeRecipe}
                reachedLimit={reachedLimit}
                isInBasket={isInBasket}
                isOutOfStock={isOutOfStock}
                index={index}
              />
            </AddRecipeButtonContainer>
          )}
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.SpaceBetween}
          height="100%"
          position={DisplayPosition.Relative}
        >
          <SoldOutOverlay backgroundColor={Color.White} isOutOfStock={isOutOfStock} />
          <Box>
            <Title title={recipe.name} />
            <Space size={2}></Space>
            <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
              {showCustomisedTileFooter ? (
                <RecipeSubtitle
                  recipe={recipe}
                  menuPreferences={menuPreferences}
                  numPortions={numPortions}
                />
              ) : (
                <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1} wrap>
                  <CookingTimeText recipe={recipe} numPortions={numPortions} />
                  {isHeatOnly && <ProteinText recipe={recipe} />}
                  {showCaloriesText && <CaloriesText recipe={recipe} />}
                </Box>
              )}
              {hasAlternativeOptions && (
                <Text fontWeight={FontWeight.Bold} style={{ visibility: 'hidden' }}>
                  Customise
                </Text>
              )}
            </Box>
          </Box>
          {showPurchaseInfo && (
            <RecipeTilePurchaseInfo isOutOfStock={isOutOfStock} recipe={recipe} />
          )}
        </Box>
      </Box>
      <RecipeTileReadyToHeatBanner recipe={recipe} isOutOfStock={isOutOfStock} />
    </Box>
  )
}
